import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Fetch the authenticated tenant's current rental status.
 * Endpoint: GET /api/rental/status
 */
export const getRentalStatus = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/rental/status`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch rental status';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getRentalStatus error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch the authenticated tenant's rent / deposit payment history for a period.
 * Endpoint: GET /api/rental/payment-history?year=&month=
 */
export const getPaymentHistory = async (year, month) => {
  const params = new URLSearchParams();
  params.set('year', String(year));
  params.set('month', String(month));

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/rental/payment-history?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch payment history';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPaymentHistory error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List rent evidence submitted by the authenticated tenant (all statuses).
 * Endpoint: GET /api/rental/rent-evidence
 */
export const getTenantRentEvidence = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/rental/rent-evidence`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch rent evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getTenantRentEvidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit or resubmit monthly rent evidence (tenant).
 * Same endpoint for first submission and resubmit after REJECTED.
 * Endpoint: POST /api/rental/rent-evidence
 * @param {{ assignmentId: string, year: number, month: number, amountPaid: number, mpesaMessage: string, photoUrl?: string }} body
 */
export const submitRentEvidence = async (body) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/rental/rent-evidence`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assignmentId: body.assignmentId,
          year: Number(body.year),
          month: Number(body.month),
          amountPaid: Number(body.amountPaid),
          mpesaMessage: body.mpesaMessage ?? '',
          photoUrl: body.photoUrl ?? '',
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to submit rent evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('submitRentEvidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch the authenticated tenant's past and current tenancies.
 * Endpoint: GET /api/rental/tenancy-history
 */
export const getTenancyHistory = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/rental/tenancy-history`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch tenancy history';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getTenancyHistory error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit a tenant move-out request for a specific property and unit.
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/move-out-request
 * @param {string} propertyId
 * @param {string} unitId
 * @param {{ moveOutDate: string, reason?: string }} body
 */
export const submitMoveOutRequest = async (propertyId, unitId, body) => {
  if (!propertyId || !unitId) {
    throw new Error('Property and unit are required for move-out request.');
  }

  const moveOutDate = String(body?.moveOutDate || '').trim();
  if (!moveOutDate) {
    throw new Error('Move-out date is required.');
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${encodeURIComponent(propertyId)}/units/${encodeURIComponent(unitId)}/move-out-request`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          moveOutDate,
          reason: String(body?.reason || 'No reason'),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to submit move-out request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('submitMoveOutRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch tenant move-out requests by notice status.
 * Endpoint: GET /api/tenants/move-out-requests/my?status=
 * @param {'PENDING'|'APPROVED'|'REJECTED'|string} status
 */
export const getMyMoveOutRequests = async (status = 'PENDING') => {
  const params = new URLSearchParams();
  params.set('status', String(status || 'PENDING').toUpperCase());

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/move-out-requests/my?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch move-out requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getMyMoveOutRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Fetch move-out requests for a specific property by status.
 * Endpoint: GET /api/tenants/move-out-requests?propertyId=&status=
 * @param {string} propertyId
 * @param {'PENDING'|'APPROVED'|'REJECTED'|string} status
 */
export const getPropertyMoveOutRequests = async (propertyId, status = 'PENDING') => {
  if (!propertyId) {
    throw new Error('Property is required to fetch move-out requests.');
  }

  const params = new URLSearchParams();
  params.set('propertyId', String(propertyId));
  params.set('status', String(status || 'PENDING').toUpperCase());

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/move-out-requests?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch property move-out requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPropertyMoveOutRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve or reject a move-out request decision for an assignment.
 * Endpoint: POST /api/tenants/assignments/{assignmentId}/move-out/decision
 * @param {string} assignmentId
 * @param {{ decision: 'APPROVE'|'REJECT'|string, moveOutYear: number, moveOutMonth: number, reason?: string }} body
 */
export const decideMoveOutRequest = async (assignmentId, body) => {
  if (!assignmentId) {
    throw new Error('Assignment is required for move-out decision.');
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/assignments/${encodeURIComponent(assignmentId)}/move-out/decision`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision: String(body?.decision || '').toUpperCase(),
          ...(body?.moveOutDate ? { moveOutDate: body.moveOutDate } : {
            moveOutYear: Number(body?.moveOutYear),
            moveOutMonth: Number(body?.moveOutMonth),
          }),
          reason: String(body?.reason || ''),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to submit move-out decision';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('decideMoveOutRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Issue an eviction notice for an active tenant assignment.
 * Endpoint: POST /api/tenants/assignments/{assignmentId}/eviction-notice
 * @param {string} assignmentId
 * @param {{ moveOutYear: number, moveOutMonth: number, reason: string }} body
 */
export const issueEvictionNotice = async (assignmentId, body) => {
  if (!assignmentId) {
    throw new Error('Assignment is required to issue eviction notice.');
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/assignments/${encodeURIComponent(assignmentId)}/eviction-notice`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(body?.moveOutDate ? { moveOutDate: body.moveOutDate } : {
            moveOutYear: Number(body?.moveOutYear),
            moveOutMonth: Number(body?.moveOutMonth),
          }),
          reason: String(body?.reason || ''),
        }),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to issue eviction notice';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('issueEvictionNotice error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Release a tenant assignment manually without an eviction or move-out request.
 * Endpoint: POST /api/tenants/assignments/{assignmentId}/release
 * @param {string} assignmentId
 * @param {string} releaseDate - Optional. Omit or leave empty for today.
 */
export const releaseTenant = async (assignmentId, releaseDate) => {
  if (!assignmentId) {
    throw new Error('Assignment is required to release tenant.');
  }

  const payload = {};
  if (releaseDate) {
    payload.releaseDate = releaseDate;
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/assignments/${encodeURIComponent(assignmentId)}/release`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined,
      },
      true
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to release tenant';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('releaseTenant error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Recall/cancel an eviction notice for a tenant assignment.
 * Endpoint: POST /api/tenants/assignments/{assignmentId}/eviction-recall
 * @param {string} assignmentId
 */
export const recallEvictionNotice = async (assignmentId) => {
  if (!assignmentId) {
    throw new Error('Assignment is required to recall eviction notice.');
  }

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/assignments/${encodeURIComponent(assignmentId)}/eviction-recall`,
      {
        method: 'POST',
      },
      true
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to recall eviction notice';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('recallEvictionNotice error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List tenant score access requests for authenticated tenant.
 * Endpoint: GET /api/tenants/score-access-requests
 */
export const getTenantScoreAccessRequests = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/score-access-requests`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch score access requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getTenantScoreAccessRequests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve a tenant score access request.
 * Endpoint: POST /api/tenants/score-access-requests/{scoreAccessRequestId}/approve
 * @param {string} scoreAccessRequestId
 */
export const approveTenantScoreAccessRequest = async (scoreAccessRequestId) => {
  const requestId = String(scoreAccessRequestId || '').trim();
  if (!requestId) throw new Error('Score access request is required.');

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/score-access-requests/${encodeURIComponent(requestId)}/approve`,
      {
        method: 'POST',
      },
      true
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to approve score access request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('approveTenantScoreAccessRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a tenant score access request.
 * Endpoint: POST /api/tenants/score-access-requests/{scoreAccessRequestId}/reject
 * @param {string} scoreAccessRequestId
 * @param {string} reason
 */
export const rejectTenantScoreAccessRequest = async (scoreAccessRequestId, reason) => {
  const requestId = String(scoreAccessRequestId || '').trim();
  const reasonText = String(reason || '').trim();
  if (!requestId) throw new Error('Score access request is required.');
  if (!reasonText) throw new Error('Rejection reason is required.');

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/tenants/score-access-requests/${encodeURIComponent(requestId)}/reject`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reasonText }),
      },
      true
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to reject score access request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('rejectTenantScoreAccessRequest error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

